import { useEffect, useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import {
  BackButton,
  Breadcrums,
  MUIButton,
  MUIGrid,
  MUIPagination,
  SimpleDropdown,
} from '../../Components/Shared'
import CONSTANTS, { BREADCRUMS, PAGE_NAME } from '../../Constants'
import { BreadcrumsModel } from '../../Models'
import './AllNotifications.scss'
import { NOTIFICATIONS } from '../../Constants/ApiDefinations'
import mainApiService from '../../Services'
import { Typography } from '@mui/material'
import ListingFilter from '../../Components/Atoms/ListingFilter'
import { getNotificationFilterFields } from '../../Constants/Constant'
import { NotificationTabType } from '../../Models/CommonListHeadingModal'
import { logger } from '../../Helpers'
import { getNotificationRoute } from '../../Helpers/NotificationRouteResolver'
import {
  getSessionStorageForRoute,
  setSessionStorage,
  removeSessionStorage,
} from '../../Helpers/Util'

const NOTIFICATION_FILTERS_KEY = 'notificationFilters'

interface NotificationDateRange {
  fromDate: string | null
  toDate: string | null
}

interface PersistedNotificationFilters {
  filters: any[]
  dateRange: NotificationDateRange
}

const AllNotification = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()

  const activeNotificationTab = searchParams.get('tab')

  const readPersistedFilters = (): PersistedNotificationFilters => {
    const routeData = getSessionStorageForRoute(location.pathname)
    const stored = routeData?.[NOTIFICATION_FILTERS_KEY]
    return {
      filters: Array.isArray(stored?.filters) ? stored.filters : [],
      dateRange: stored?.dateRange ?? { fromDate: null, toDate: null },
    }
  }

  const [tab, setTab] = useState<NotificationTabType>(() =>
    activeNotificationTab === 'others' ? 'others' : 'my',
  )
  const [notifications, setNotifications] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(10)
  const [totalCount, setTotalCount] = useState(0)
  const [filters, setFilters] = useState<any[]>(() => readPersistedFilters().filters)
  const [clearFilterTrigger, setClearFilterTrigger] = useState(0)
  const [eventOptions, setEventOptions] = useState<{ key: string; value: string }[]>([])
  const [dateRange, setDateRange] = useState<NotificationDateRange>(
    () => readPersistedFilters().dateRange,
  )
  const [eventOptionsLoading, setEventOptionsLoading] = useState(true)

  const filterFields = getNotificationFilterFields(eventOptions)

  const buildApiFilters = (filters: any[]) => {
    return filters
      .filter((filter) => filter.filterBy === 'event' || filter.filterBy === 'readStatus')
      .map((filter) => {
        if (filter.filterBy === 'event') {
          return {
            filterBy: 'eventCode',
            filterCondition: 'in',
            filterValue: filter.filterValue,
          }
        }
        if (filter.filterBy === 'readStatus') {
          return {
            filterBy: 'isRead',
            filterCondition: 'equals',
            filterValue: filter.filterValue[0] === 'read',
          }
        }
        return null
      })
      .filter(Boolean)
  }

  const fetchNotifications = async (
    myNotification: boolean,
    pageNum = page,
    pageLimit = limit,
    appliedFilters = filters,
  ) => {
    setLoading(true)
    try {
      const apiFilters = buildApiFilters(appliedFilters)
      const payload: {
        myNotification: boolean
        filters: ({ filterBy: string; filterCondition: string; filterValue: any } | null)[]
        sortType: number
        page: number
        limit: number
        fromDate?: string | null
        toDate?: string | null
      } = {
        myNotification,
        filters: apiFilters,
        sortType: -1,
        page: pageNum,
        limit: pageLimit,
      }
      if (dateRange.fromDate) payload.fromDate = dateRange.fromDate
      if (dateRange.toDate) payload.toDate = dateRange.toDate
      const notificationList = NOTIFICATIONS.NOTIFICATION_LIST(payload)
      const response: any = await mainApiService(notificationList)
      setNotifications(
        Array.isArray(response.data?.notifications) ? response.data.notifications : [],
      )
      setTotalCount(response?.data?.count || 0)
    } catch (error) {
      setNotifications([])
      setTotalCount(0)
      logger.error(error)
    }
    setLoading(false)
  }

  useEffect(() => {
    const fetchEvents = async () => {
      setEventOptionsLoading(true)
      try {
        const eventsResponse: any = await mainApiService(NOTIFICATIONS.NOTIFICATION_EVENTS())
        const options = (eventsResponse?.data || []).map((e: any) => ({
          key: e.eventCode,
          value: e.label,
        }))
        setEventOptions(options)
      } catch {
        setEventOptions([])
      } finally {
        setEventOptionsLoading(false)
      }
    }
    fetchEvents()
  }, [])
  useEffect(() => {
    if (!activeNotificationTab) return
    if (activeNotificationTab === 'my' || activeNotificationTab === 'others') {
      setTab((prev) => (prev === activeNotificationTab ? prev : activeNotificationTab))
    } else {
      setSearchParams({ tab: 'my' }, { replace: true })
    }
  }, [activeNotificationTab])
  useEffect(() => {
    fetchNotifications(tab === 'my', page, limit, filters)
  }, [tab, page, limit, filters, dateRange])

  useEffect(() => {
    const hasActiveFilters = filters.length > 0 || dateRange.fromDate || dateRange.toDate
    const payload: PersistedNotificationFilters | null = hasActiveFilters
      ? { filters, dateRange }
      : null
    setSessionStorage(NOTIFICATION_FILTERS_KEY, payload, location.pathname)
    if (!hasActiveFilters) {
      removeSessionStorage(NOTIFICATION_FILTERS_KEY)
    }
  }, [filters, dateRange, location.pathname])

  const handleMarkAllAsRead = async () => {
    try {
      const payload = { markAllAsRead: true }
      await mainApiService(NOTIFICATIONS.NOTIFICATION_MARK_AS_READ(payload))
      fetchNotifications(tab === 'my', page, limit, filters)
    } catch (error) {
      logger.error(error)
    }
  }

  const handleNotificationClick = async (item: any) => {
    try {
      const notificationId = item?.userNotificationStatus?.notificationId
      if (notificationId) {
        const payload = { markAllAsRead: false, notificationId }
        await mainApiService(NOTIFICATIONS.NOTIFICATION_MARK_AS_READ(payload))
      }
      const route = getNotificationRoute(item)
      if (route) {
        navigate(route)
      }
    } catch (error) {
      logger.error(error)
    }
  }

  const handlePerPageView = (e: any) => {
    setLimit(Number(e.target.value))
    setPage(1)
  }

  const handlePaginationChange = (_event: any, value: number) => {
    setPage(value)
  }

  const handleFilterSubmit = (filterObj: any, id?: string) => {
    if (!filterObj || Object.keys(filterObj).length === 0) {
      setFilters((prev: any[]) => prev.filter((filter) => filter.filterBy !== id))
      if (id === 'date') setDateRange({ fromDate: null, toDate: null })
      setPage(1)
      return
    }
    if (filterObj.filterBy === 'date' && filterObj.filterCondition === 'range') {
      setDateRange({
        fromDate: filterObj.filterValue?.fromDate ?? null,
        toDate: filterObj.filterValue?.toDate ?? null,
      })
      setPage(1)
      return
    }
    setFilters((prev: any[]) => {
      const filtered = prev.filter((filter) => filter.filterBy !== filterObj.filterBy)
      if (!filterObj.filterValue || filterObj.filterValue.length === 0) {
        return filtered
      }
      return [...filtered, filterObj]
    })
    setPage(1)
  }
  const handleTabClick = (tabTitle: NotificationTabType) => {
    setTab(tabTitle)
    setPage(1)
    if (activeNotificationTab) {
      searchParams.delete('tab')
      setSearchParams(searchParams)
    }
  }
  const hasUnread = notifications.some(
    (item) => item.userNotificationStatus && !item.userNotificationStatus.isRead,
  )
  const displayNotificationItemList = () => {
    if (loading) return <div style={{ padding: 24, textAlign: 'center' }}>Loading...</div>
    if (notifications.length === 0)
      return (
        <div className='notification-empty'>
          <div className='notification-list-empty-title'>No results found.</div>
          <div className='notification-list-empty-desc'>
            You can modify the filters or reset them to view all results.
          </div>
        </div>
      )

    return notifications.map((item, idx) => (
      <button
        key={item._id + idx}
        className={`notification-item${item.userNotificationStatus?.isRead ? '' : ' unread'}`}
        type='button'
        onClick={() => handleNotificationClick(item)}
        data-testid={`${item.title.toLowerCase()} ${item._id}`}
      >
        <div
          className='notification-title-row'
          style={{ display: 'flex', alignItems: 'center', position: 'relative' }}
        >
          <div className='notification-list-item-title'>{item.title}</div>
          {!item.userNotificationStatus?.isRead && <span className='notification-list-dot' />}
        </div>
        <div className='notification-list-item-desc'>{item.description}</div>
        <div className='notification-list-item-date'>
          {item.updatedAt
            ? `${new Date(item.updatedAt).toLocaleDateString()} ${new Date(
                item.updatedAt,
              ).toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit',
                hour12: false,
              })}`
            : ''}
        </div>
      </button>
    ))
  }

  return (
    <div className='listing-screen p-16'>
      <form>
        <div style={{ paddingTop: '8px', paddingBottom: '8px' }}>
          <Breadcrums breadcrums={BREADCRUMS.ALL_NOTIFICATION() as BreadcrumsModel[]} />
        </div>
        <div
          style={{ display: 'flex', alignItems: 'center', paddingTop: '8px', paddingBottom: '8px' }}
        >
          <BackButton
            label={PAGE_NAME.AllNotification}
            className='col73'
            handleOnClick={() => navigate(-1)}
          />
          <button
            className='notification-button border-none transparent-background'
            onClick={hasUnread ? handleMarkAllAsRead : undefined}
            style={{
              cursor: hasUnread ? 'pointer' : 'default',
              opacity: hasUnread ? 1 : 0.5,
              pointerEvents: hasUnread ? 'auto' : 'none',
              color: '#402080',
            }}
          >
            Mark All as Read
          </button>
        </div>
        <div className='notification-header-row'>
          <div className='notification-tab'>
            <div style={{ display: 'flex' }}>
              <button
                className={`notification-tab${
                  tab === 'my' ? ' selected' : ''
                } border-none transparent-background`}
                type='button'
                onClick={() => handleTabClick('my')}
              >
                <span className='notif-tab-text'> My Notifications </span>
              </button>
              <button
                className={`notification-tab${
                  tab === 'others' ? ' selected' : ''
                } border-none transparent-background`}
                type='button'
                onClick={() => handleTabClick('others')}
              >
                <span className='notif-tab-text'>Others</span>
              </button>
            </div>
          </div>
          <div className='notification-filter'>
            {(filters.length > 0 || dateRange.fromDate || dateRange.toDate) && (
              <MUIButton
                label='Clear All'
                variant='text'
                fontSize='16px'
                width={100}
                className='clear-filters-btn'
                type='button'
                handleClickCB={() => {
                  setFilters([])
                  setDateRange({ fromDate: null, toDate: null })
                  setClearFilterTrigger((prev) => prev + 1)
                }}
              />
            )}
            {filterFields.map((item) => (
              <ListingFilter
                key={item.id}
                filters={filters.find((it: any) => it.filterBy === item.id)?.filterValue || []}
                searchable={item.searchable}
                lable={item.lable}
                id={item.id}
                options={item.options ?? []}
                searchFunction={handleFilterSubmit}
                isFilterSingleSelect={item.isFilterSingleSelect}
                clearFilter={true}
                clearFilterTrigger={clearFilterTrigger}
                type={
                  item.type === 'dateRange'
                    ? 'dateRange'
                    : item.type === 'date'
                      ? 'date'
                      : undefined
                }
                initialDateRange={
                  item.id === 'date' && (dateRange.fromDate || dateRange.toDate)
                    ? {
                        fromDate: dateRange.fromDate,
                        toDate: dateRange.toDate,
                        quickRange: 'custom',
                      }
                    : null
                }
                isLoading={item.id === 'event' ? eventOptionsLoading : false}
              />
            ))}
          </div>
        </div>
        <div className='notification-content-wrapper'>
          <div className='notification-table'>{displayNotificationItemList()}</div>
          <MUIGrid
            container
            className='d-flex align-items-center justify-content-between c-pagination notification-pagination'
          >
            {notifications.length > 0 && (
              <MUIGrid item direction='row' className='d-flex align-items-center col1'>
                <SimpleDropdown
                  selectId='pageId'
                  options={CONSTANTS.PAGINATION_OPTIONS}
                  handleChange={handlePerPageView}
                  value={limit}
                />
                <Typography paddingX={3}>
                  Showing {notifications.length} of {totalCount} records
                </Typography>
              </MUIGrid>
            )}
            <MUIGrid item>
              {notifications.length > 0 && totalCount > 0 && (
                <MUIPagination
                  testId='userPagination'
                  onChange={handlePaginationChange}
                  count={Math.ceil(totalCount / limit)}
                  page={page}
                />
              )}
            </MUIGrid>
          </MUIGrid>
        </div>
      </form>
    </div>
  )
}

export default AllNotification
